import React, { useEffect, useMemo, useState } from "react";
import { Button, TabContent, TabPane } from "reactstrap";
import ClientType from "./ClientType";
import WorkType from "./WorkType";
import IndustryType from "./IndustryType";
import FinancialYear from "./FinancialYearList";
import Risk from "./Risk";
import ReviewType from "./ReviewType";
import {
  getCompanyDetails,
  getLoggedInUser,
} from "../../helpers/backend_helper";
import "./Masters.scss";

const masterTabs = [
  {
    id: 7,
    icon: "bx-calendar-check",
    label: "Review Types",
    singular: "Review Type",
  },
  {
    id: 2,
    icon: "bx-briefcase-alt-2",
    label: "Work Types",
    singular: "Work Type",
  },
  { id: 3, icon: "bx-group", label: "Client Types", singular: "Client Type" },
  {
    id: 4,
    icon: "bx-buildings",
    label: "Industry Types",
    singular: "Industry Type",
  },
  {
    id: 5,
    icon: "bx-calendar",
    label: "Financial Years",
    singular: "Financial Year",
  },
  { id: 6, icon: "bx-shield-quarter", label: "Risks", singular: "Risk" },
];

const MeetingMastersIndex = () => {
  const [activeTab, setActiveTab] = useState(7);
  const [createRequest, setCreateRequest] = useState({ tab: 0, version: 0 });
  const [tourRequest, setTourRequest] = useState({ tab: 0, version: 0 });
  const [expandedGuide, setExpandedGuide] = useState("about");
  const [includeRisks, setIncludeRisks] = useState(true);
  const visibleMasterTabs = useMemo(
    () =>
      includeRisks ? masterTabs : masterTabs.filter((tab) => tab.id !== 6),
    [includeRisks]
  );
  const activeMaster =
    visibleMasterTabs.find((tab) => tab.id === activeTab) ||
    visibleMasterTabs[0];

  useEffect(() => {
    let current = true;
    getLoggedInUser()
      .then((user) => (user?.firmId ? getCompanyDetails(user.firmId) : null))
      .then((response) => {
        if (!current || !response?.data) return;
        const risksEnabled = response.data.includeInspectionRisks !== false;
        setIncludeRisks(risksEnabled);
        if (!risksEnabled) setActiveTab((value) => (value === 6 ? 7 : value));
      })
      .catch(() => undefined);
    return () => {
      current = false;
    };
  }, []);

  const requestCreate = () => {
    setCreateRequest((current) => ({
      tab: activeTab,
      version: current.version + 1,
    }));
  };

  return (
    <div className="page-content masters-page piers-page">
      <section className="masters-page__shell">
        <header className="masters-page__title piers-page-header">
          <h1 className="piers-page-title">References</h1>
          <p>
            Manage the reference data used across engagements, reviews, and
            reporting.
          </p>
        </header>
        <header className="masters-page__tabs">
          <nav aria-label="Master data sections">
            {visibleMasterTabs.map((tab) => (
              <button
                key={tab.id}
                className={activeTab === tab.id ? "is-active" : ""}
                type="button"
                onClick={() => setActiveTab(tab.id)}
              >
                <i className={`bx ${tab.icon}`} /> {tab.label}
              </button>
            ))}
          </nav>
          <div className="masters-page__actions">
            <Button color="primary" type="button" onClick={requestCreate}>
              <i className="bx bx-plus" /> New {activeMaster.singular}
            </Button>
          </div>
        </header>

        <main className="masters-page__workspace piers-page">
          <section className="masters-page__content">
            <TabContent activeTab={activeTab}>
              <TabPane tabId={7}>
                <ReviewType
                  createRequest={
                    createRequest.tab === 7 ? createRequest.version : 0
                  }
                  tourRequest={tourRequest.tab === 7 ? tourRequest.version : 0}
                />
              </TabPane>
              <TabPane tabId={2}>
                <WorkType
                  createRequest={
                    createRequest.tab === 2 ? createRequest.version : 0
                  }
                  tourRequest={tourRequest.tab === 2 ? tourRequest.version : 0}
                />
              </TabPane>
              <TabPane tabId={3}>
                <ClientType
                  createRequest={
                    createRequest.tab === 3 ? createRequest.version : 0
                  }
                  tourRequest={tourRequest.tab === 3 ? tourRequest.version : 0}
                />
              </TabPane>
              <TabPane tabId={4}>
                <IndustryType
                  createRequest={
                    createRequest.tab === 4 ? createRequest.version : 0
                  }
                  tourRequest={tourRequest.tab === 4 ? tourRequest.version : 0}
                />
              </TabPane>
              <TabPane tabId={5}>
                <FinancialYear
                  createRequest={
                    createRequest.tab === 5 ? createRequest.version : 0
                  }
                />
              </TabPane>
              {includeRisks && (
                <TabPane tabId={6}>
                  {activeTab === 6 && (
                    <Risk
                      createRequest={
                        createRequest.tab === 6 ? createRequest.version : 0
                      }
                    />
                  )}
                </TabPane>
              )}
            </TabContent>
          </section>

          <aside className="masters-help-guide">
            <header>
              <h2>Help Guide</h2>
              <button type="button" aria-label="Close help guide">
                <i className="bx bx-x" />
              </button>
            </header>
            <section>
              <button
                className={expandedGuide === "about" ? "is-expanded" : ""}
                type="button"
                onClick={() => setExpandedGuide("about")}
              >
                <i className="bx bx-info-circle" />
                <span>About {activeMaster.label}</span>
                <i className="bx bx-chevron-right" />
              </button>
              {expandedGuide === "about" && (
                <p>
                  {activeMaster.label} help you classify and organize records
                  during engagement reviews. They can be selected while creating
                  and managing engagements.
                </p>
              )}
            </section>
            {["Creating", "Best Practices", "Frequently Asked Questions"].map(
              (item, index) => (
                <section key={item}>
                  <button type="button" onClick={() => setExpandedGuide(item)}>
                    <i
                      className={`bx ${
                        index === 0
                          ? "bx-git-branch"
                          : index === 1
                          ? "bx-book-open"
                          : "bx-help-circle"
                      }`}
                    />
                    <span>
                      {item === "Creating"
                        ? `Creating ${activeMaster.label}`
                        : item}
                    </span>
                    <i className="bx bx-chevron-right" />
                  </button>
                  {expandedGuide === item && (
                    <p>
                      Helpful guidance for managing{" "}
                      {activeMaster.label.toLowerCase()}.
                    </p>
                  )}
                </section>
              )
            )}
            <section className="masters-help-guide__tour">
              <i className="bx bx-compass" />
              <h3>New to Masters?</h3>
              <p>Take a quick tour to learn how to manage master data.</p>
              <Button
                outline
                type="button"
                onClick={() =>
                  setTourRequest((current) => ({
                    tab: activeTab,
                    version: current.version + 1,
                  }))
                }
              >
                <i className="bx bx-play" /> Start Guided Tour
              </Button>
            </section>
          </aside>
        </main>
      </section>
    </div>
  );
};

export default MeetingMastersIndex;
